import { dasherize } from '@ember/string'
import type { NormalizedInterval } from 'date-fns'

/**
 * The code for the sanitization util was inspired by
 * https://gist.github.com/barbietunnie/7bc6d48a424446c44ff4
 * and
 * https://github.com/parshap/node-sanitize-filename/blob/master/index.js
 *
 * Differences are mostly just updates for modern javascript (eg optional chaining), typescript, and a better way to
 * truncate a string by utf8 bytes.
 */

/**
 * Note that the maximum filename length should be capped at 255 bytes. We can't just use splice(0, 255) on the string
 * array or something like that, because complex characters can take up more than 1 byte - for example "a☃" is 2 chars
 * but 4 bytes.
 *
 * The original code on github uses a "truncate-utf8-bytes" library to do so, with some complicated code behind it, but
 * modern javascript allows us to do this easily with TextEncoder and TextDecoder.
 */
function truncate(sanitized: string, byteLength: number): string {
  const uint8Array = new TextEncoder().encode(sanitized)
  const truncated = uint8Array.slice(0, byteLength)
  return new TextDecoder().decode(truncated)
}

// These regex's differ slightly from those found in the original code on github, as it has removed unnecessary escape
// characters that aren't required according to eslint.
const illegalRe = /[/?<>\\:*|":]/g
// eslint-disable-next-line no-control-regex
const controlRe = /[\x00-\x1f\x80-\x9f]/g
const reservedRe = /^\.+$/
const windowsReservedRe = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i

function sanitize(input: string, replacement: string) {
  const sanitized = input
    .replace(illegalRe, replacement)
    .replace(controlRe, replacement)
    .replace(reservedRe, replacement)
    .replace(windowsReservedRe, replacement)
  // While the maximum is 255, allow some room for a standard file extension to be added like .csv
  return truncate(sanitized, 250)
}

/**
 * Function to sanitize & truncate a filename, to be safe for use. A file extension should be added separately, to avoid
 * the truncation truncating the extension.
 */
export function sanitizeFilename(input: string, options?: { replacement: string }) {
  const replacement = options?.replacement ?? ''
  const output = sanitize(input, replacement)
  if (replacement === '') {
    return output
  }
  // This additional sanitize is to ensure the replacement character given in options is safe for filenames as well.
  return sanitize(output, '')
}

/**
 * Returns a standard filename for use in a reporting export file. Can be used for files of any extension.
 *
 * The parts are dasherized, and joined with underscores. The period is formatted according to the users' time format.
 *
 * @param reportName - name of the report
 * @param scopeName - name of the scope. Could be a student name, school name, teacher or class name.
 * @param period - represents the selected time period for the report
 *
 * @returns a sanitized filename for a reporting export file (without an extension)
 */
export function reportingFilename(reportName: string, scopeName: string, period: NormalizedInterval): string {
  const start = new Intl.DateTimeFormat().format(period.start)
  const end = new Intl.DateTimeFormat().format(period.end)
  return sanitizeFilename(`${dasherize(reportName)}_${dasherize(scopeName)}_${start}-${end}`, { replacement: '-' })
}
